import React, { useRef, useEffect, useCallback } from 'react'
import {
  AccordionProps,
  AccordionItemProps,
  AccordionSectionProps
} from './Accordion.type'
import './style.module.scss'

export const AccordionItem = ({
  heading,
  subheading,
  children,
  defaultOpen = false,
  icon,
  iconAlt = 'Icon',
  id
}: AccordionItemProps) => {
  return (
    <details className="Content Accordion-item" open={defaultOpen || undefined}>
      <summary
        className="Accordion-toggle u-flexCol u-paddingVert16 u-paddingRight8"
        aria-controls={id}
      >
        {icon && (
          <img
            className="Accordion-icon u-marginRight8 u-sizeFull"
            src={icon}
            alt={iconAlt}
          />
        )}
        <span>{heading}</span>
        {subheading && <p className="u-marginTop4">{subheading}</p>}
      </summary>
      <div className="Accordion-content Content" id={id} aria-expanded="false">
        {children}
      </div>
    </details>
  )
}

export const AccordionSection = ({
  title,
  children
}: AccordionSectionProps) => {
  return (
    <>
      {title && (
        <div className="Content section-title">
          <h2>{title}</h2>
        </div>
      )}
      <div className="Content section-content">{children}</div>
    </>
  )
}

export const Accordion = ({
  children,
  transparent,
  unruled,
  bgColor,
  className,
  testId,
  sectionTitle,
  sectionContent
}: AccordionProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const isTruthy = (val: string | boolean | undefined) =>
    val === true || val === 'true' || val === ''

  const buildClassNames = useCallback(() => {
    const classNames = ['Accordion']

    if (isTruthy(transparent)) {
      classNames.push('Accordion--transparent')
    }
    if (isTruthy(unruled)) {
      classNames.push('Accordion--unruled')
    }

    classNames.push('u-bgColorWhite')

    if (className) {
      classNames.push(className)
    }

    return classNames.join(' ')
  }, [transparent, unruled, className])

  // Apply bgColor to all Accordion-content elements
  useEffect(() => {
    if (!ref.current || !bgColor) return
    const contentEls = ref.current.querySelectorAll('.Accordion-content')
    contentEls.forEach(el => {
      el.classList.remove('u-bgColorStone', 'u-bgColorMintLight10')
      switch (bgColor) {
        case 'stone':
        case 'gray':
          el.classList.add('u-bgColorStone')
          break
        case 'mint light':
          el.classList.add('u-bgColorMintLight10')
          break
      }
    })
  }, [bgColor, children])

  // Handle inner-accordion links (hash-based navigation between items)
  useEffect(() => {
    if (!ref.current) return
    const container = ref.current

    const handleInnerLink = (e: Event) => {
      e.preventDefault()
      const target = e.target as HTMLAnchorElement
      const hash = target.hash
      if (hash) {
        const linkItem = container.querySelector(
          `a[name="${hash.replace('#', '')}"]`
        )
        if (linkItem) {
          const accordionItem = linkItem.closest(
            'details.Content.Accordion-item'
          ) as HTMLDetailsElement
          if (accordionItem) {
            accordionItem.setAttribute('open', '')
            accordionItem.scrollIntoView({ behavior: 'smooth' })
          }
        }
      }
    }

    const links = container.querySelectorAll('.Accordion-content a[href]')
    const innerLinks = Array.from(links).filter(link =>
      (link as HTMLAnchorElement).hash?.includes('#inner')
    )

    innerLinks.forEach(link => {
      link.addEventListener('click', handleInnerLink)
    })

    return () => {
      innerLinks.forEach(link => {
        link.removeEventListener('click', handleInnerLink)
      })
    }
  }, [children])

  // Handle hash-based deep linking and default view on mount
  useEffect(() => {
    if (!ref.current) return
    const container = ref.current
    const hash = globalThis.location?.hash

    if (hash) {
      const hashedLink = container.querySelector(
        `a[name="${hash.replace('#', '')}"]`
      )
      if (hashedLink) {
        const item = hashedLink.closest(
          'details.Content.Accordion-item'
        ) as HTMLDetailsElement
        if (item) {
          item.setAttribute('open', '')
          item.scrollIntoView({ behavior: 'smooth' })
        }
        return
      }
    }

    // Default view: open item with name="default" or collapse first item
    const defaultLink = container.querySelector(
      '.Accordion-content a[name="default"]'
    )
    if (defaultLink) {
      const item = defaultLink.closest(
        'details.Content.Accordion-item'
      ) as HTMLDetailsElement
      if (item) {
        item.setAttribute('open', '')
      }
    } else {
      const firstItem = container.querySelector(
        'details.Accordion-item'
      ) as HTMLDetailsElement
      const firstContent = container.querySelector('.Accordion-content')
      if (firstItem && firstContent) {
        firstItem.removeAttribute('open')
        firstContent.setAttribute('aria-expanded', 'false')
      }
    }
  }, [])

  return (
    <div ref={ref} className={buildClassNames()} data-testid={testId || null}>
      {sectionTitle && (
        <div
          className="Content section-title"
          dangerouslySetInnerHTML={{ __html: `<h2>${sectionTitle}</h2>` }}
        />
      )}
      {sectionContent && (
        <div
          className="Content section-content"
          dangerouslySetInnerHTML={{ __html: sectionContent }}
        />
      )}
      {children}
    </div>
  )
}
